<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('tableActivator.pageTitle') }}</h1>
      <ts-breadcrumb :items="[
        { text: $t('home'), href: '/' },
        {
          text: $t('tableActivator.pageTitle'),
          active: true
        }
      ]" />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <ts-button outline color="info" @click.prevent="addNew">{{
            $t('add_new')
          }}</ts-button>
          <div class="d-flex ts-space-x-3 ts-items-center">
            <ts-input v-model="search" :placeholder="$t('search')" style="width: 250px" class="inline-block" />
            <ts-filter ref="filter">
              <div class="row">
                <div class="col-md-12">
                  <label class="">{{ $t('employeeProfile.branch') }}</label>
                  <ts-branch-filter @filter="branchFilter" class="ts-w-full"></ts-branch-filter>
                </div>
              </div>
            </ts-filter>
          </div>
        </div>
      </ts-card-wrapper>
      <Table :columns="columns" :data="resources" size="small" :loading="loading" highlight-row stripe
        max-height="700">
        <template slot-scope="{ row }" slot="status">
          <span class="badge badge-success" v-if="row.status_id == 1">
            {{ row.status_name }}
          </span>
          <span class="badge badge-warning" v-else-if="row.status_id == 2">
            {{ row.status_name }}
          </span>
          <span class="badge badge-danger" v-else>
            {{ row.status_name }}
          </span>
        </template>
        <template slot-scope="{ row }" slot="activate_date">
          {{ row.activate_date }}
        </template>
        <template slot-scope="{ row }" slot="expiry_date">
          {{ row.expiry_date ? row.expiry_date : 'N/A' }}
        </template>
        <template slot-scope="{ row }" slot="action">
          <a href="#" v-tooltip="$t('edit')" class="text-primary mr-2" @click.prevent="edit(row)">
            <i class="fas fa-random"></i>
          </a>
          <a-popconfirm placement="leftTop" :title="$t('are_you_sure_to_delete')" :ok-text="$t('yes')"
            :cancel-text="$t('no')" @confirm="deleteRecord(row)">
            <a href="#" v-tooltip="$t('delete')" :disabled="row._deleting">
              <i class="fas fa-circle-notch spin" v-if="row._deleting"></i>
              <i class="fa fa-trash text-red" v-else></i>
            </a>
          </a-popconfirm>
        </template>
      </Table>
      <div class="d-flex justify-content-between align-items-center p-15">
        <ts-total-list v-model="pagination" />
        <ts-pagination v-model="pagination" @navigate="fetchData"></ts-pagination>
      </div>
      <!-- form-action -->
      <a-modal v-if="showForm" v-model="showForm" :title="$t('tableActivator.pageTitle')" centered @cancel="clearEdit"
        :zIndex="1020" :footer="null" width="600px">
        <from-action @cancel="clearEdit" @fetchData="fetchData" />
      </a-modal>
      <!-- form-action -->
      <a-modal v-if="showFormUpdate" v-model="showFormUpdate" :title="$t('tableActivator.pageTitle')" centered
        @cancel="clearEdit" :zIndex="1020" :footer="null" width="600px">
        <from-change-status @cancel="clearEdit" @fetchData="fetchData" />
      </a-modal>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import FromAction from './form'
import FromChangeStatus from './form-change-status'

export default {
  name: 'tableActivatorIndex',
  components: {
    FromAction,
    FromChangeStatus,
  },
  data() {
    return {
      loading: false,
      showForm: false,
      showFormUpdate: false
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    ...mapState('booking/tableActivator', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.booking.tableActivator.search
      },
      set(newValue) {
        this.$store.commit('booking/tableActivator/SET_SEARCH', newValue)
        this.$store.commit('booking/tableActivator/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        {
          title: this.$t('tableActivator.branch'),
          key: 'branch_name_en',
          width: 200,
        },
        {
          title: this.$t('tableActivator.table'),
          key: 'table_no',
          width: 200,
        },
        {
          title: this.$t('tableActivator.guestType'),
          key: 'guest_level_name_en',
          width: 200,
          align: 'center',
        },
        {
          title: this.$t('tableActivator.serialCard'),
          key: 'serial_card',
          width: 200,
        },
        {
          title: this.$t('tableActivator.status'),
          slot: 'status',
          align: 'center',
          width: 150,
        },
        {
          title: this.$t('tableActivator.activateDate'),
          slot: 'activate_date',
          align: 'center',
          width: 200,
        },
        {
          title: this.$t('tableActivator.expiryDate'),
          slot: 'expiry_date',
          align: 'center',
          width: 200,
        },
        {
          title: this.$t('tableActivator.createdDate'),
          key: 'created_date',
          width: 200,
        },
        {
          title: this.$t('actions'),
          key: 'action',
          slot: 'action',
          fixed: 'right',
          align: 'center',
          className: 'ts-space-x-1',
          width: 94
        }
      ]
    }
  },
  methods: {
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('booking/tableActivator/fetch', { ...attributes })
        .then(response => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    addNew() {
      this.showForm = true
    },
    edit(record) {
      this.$store.commit('booking/tableActivator/SET_EDIT_DATA', record)
      this.showFormUpdate = true
    },
    clearEdit() {
      this.$store.commit('booking/tableActivator/SET_EDIT_DATA', {})
      this.showForm = false
      this.showFormUpdate = false
      this.fetchData()
    },
    deleteRecord(record) {
      record._deleting = true
      this.$store
        .dispatch('booking/tableActivator/destroy', record.activator_id)
        .then(response => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          record._deleting = false
        })
    },
    branchFilter(value) {
      this.$store.commit('booking/tableActivator/SET_BRANCH_ID', value)
      this.fetchData()
    }
  },
  watch: {
    search: debounce(function (search) {
      this.fetchData()
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('booking/tableActivator/RESET_STATE')
    next()
  }
}
</script>
